import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Layout } from '../components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Eye, Edit, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const Devices = () => {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    sort_by: 'created_at',
    sort_order: 'desc'
  });
  const [formEditData, setFormEditData] = useState({
    id: null,
    customer_id: '',
    property_id: '',
    device_number: '',
    serial_number: '',
    device_name: '',
    device_type: '',
    firmware_version: '',
    hardware_version: '',
    installation_date: ''
  });
  const [formEditBalance, setFormEditBalance] = useState({
    id: null,
    balance: 0
  });
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditBalanceModal, setShowEditBalanceModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchDevices();
    fetchCustomers();
    fetchProperties();
  }, [filters]);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
        ...(filters.status && { status: filters.status }),
        ...(filters.search && { search: filters.search }),
      });
      
      const response = await axios.get(`${API}/devices?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setDevices(response.data);
    } catch (error) {
      // Only show error toast if not initial load
      if (!loading) {
        toast.error('Error loading devices');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/customers-options`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/properties`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to fetch properties');
    } finally {
      setLoading(false);
    }
  };

  const deviceOptions = [
    { value: "flow_sensor", label: "Flow Sensor" },
    { value: "pressure_sensor", label: "Pressure Sensor" },
    { value: "quality_sensor", label: "Quality Sensor" },
    { value: "smart_valve", label: "Smart Valve" },
    { value: "water_meter", label: "Water Meter" },
  ];

  const handleSelectDevice = (deviceId) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId) 
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDevices.length === devices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(devices.map(d => d.id));
    }
  };

  const handleBatchOperation = async (operation) => {
    if (selectedDevices.length === 0) {
      toast.error('Please select at least one device');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to ${operation} ${selectedDevices.length} device(s)?`
    );
    
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user'));
      
      const response = await fetch(`${API}/devices/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          device_ids: selectedDevices,
          action: operation,
          performed_by: user.id
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setSelectedDevices([]);
        setShowBatchModal(false);
        fetchDevices();
      } else {
        toast.error('Batch operation failed');
      }
    } catch (error) {
      console.error('Error performing batch operation:', error);
      toast.error('Error performing operation');
    }
  };

  const handleDeviceClick = (device) => {
    setSelectedDevice(device);
    setShowDetailModal(true);
  };

  const handleEditDevice = (device) => {
    setFormEditData({
      id: device.id,
      customer_id: device.customer_id,
      property_id: device.property_id,
      device_number: device.device_number,
      serial_number: device.serial_number,
      device_name: device.device_name,
      device_type: device.device_type,
      firmware_version: device.firmware_version,
      hardware_version: device.hardware_version,
      installation_date: device.installed_at?.split('T')[0] ?? ''
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
  };

  const handleEditBalance = (device) => {
    setFormEditBalance({
      id: device.id,
      balance: device.balance ?? 0,
    });
    setShowEditBalanceModal(true);
  };

  const closeEditBalanceModal = () => {
    setShowEditBalanceModal(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      maintenance: 'bg-yellow-100 text-yellow-800',
      faulty: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getHealthColorBackup = (healthStatus) => {
    const colors = {
      excellent: 'text-green-600',
      good: 'text-blue-600',
      fair: 'text-yellow-600',
      poor: 'text-red-600'
    };
    return colors[healthStatus] || 'text-gray-600';
  };

  const getHealthColor = (healthScore) => {
    if (!healthScore && healthScore !== 0) return 'text-gray-600';
    if (healthScore >= 90) return 'bg-green-100 text-green-600';
    if (healthScore >= 75) return 'bg-blue-100 text-blue-600';
    if (healthScore >= 50) return 'bg-yellow-100 text-yellow-600';
    return 'bg-red-100 text-red-600';
  };

  const getHealthText = (healthScore) => {
    if (!healthScore && healthScore !== 0) return 'Unknown';
    if (healthScore >= 90) return 'Excellent';
    if (healthScore >= 75) return 'Good';
    if (healthScore >= 50) return 'Fair';
    return 'Poor';
  };

  const getConnectionColor = (status) => {
    const colors = {
      online: 'bg-green-500',
      offline: 'bg-red-500',
      idle: 'bg-yellow-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      await axios.put(`${API}/devices/${formEditData.id}`, formEditData, { headers });
      toast.success('Device updated successfully');

      fetchDevices();
      closeEditModal();
    } catch (error) {
      console.error('Error updating device:', error);
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleUpdateBalance = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      await axios.put(`${API}/device-balance/${formEditBalance.id}`, formEditBalance, { headers });
      toast.success('Device balance updated successfully');

      fetchDevices();
      closeEditBalanceModal();
    } catch (error) {
      console.error('Error updating device balance:', error);
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Device Management</h1>
          <p className="text-gray-600">Comprehensive view of all water meters and devices</p>
        </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Devices</p>
              <p className="text-2xl font-bold text-gray-900">{devices.length || 0}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-green-600">
                  {devices.filter(d => d.status === 'active').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Online</p>
              <p className="text-2xl font-bold text-blue-600">
                  {devices.filter(d => d.connection_status === 'online').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Alerts</p>
              <p className="text-2xl font-bold text-red-600">0</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search devices by ID or name..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
            />
          </div>

          {/* Status Filter */}
          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="maintenance">Maintenance</option>
            <option value="faulty">Faulty</option>
          </select>

          {/* Sort */}
          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            value={filters.sort_by}
            onChange={(e) => setFilters({...filters, sort_by: e.target.value})}
          >
            <option value="created_at">Date Created</option>
            <option value="device_name">Device Name</option>
            <option value="status">Status</option>
          </select>

          {/* Batch Actions */}
          {selectedDevices.length > 0 && (
            <button
              onClick={() => setShowBatchModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              Batch Actions ({selectedDevices.length})
            </button>
          )}
        </div>
      </div>

      {/* Devices Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : devices.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
            <p className="mt-2 text-gray-500">No devices found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedDevices.length === devices.length}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Health
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {devices.map((device) => (
                  <tr key={device.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedDevices.includes(device.id)}
                        onChange={() => handleSelectDevice(device.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3 ${getConnectionColor(device.iot_device?.connection_status || 'offline')}`}></div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{device.device_name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{device.customer.full_name || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{device.property.property_name || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(device.status)}`}>
                        {device.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getHealthColor(device.health_score)}`}>
                        {device.health_score?.toFixed(0) || 0}% - {getHealthText(device.health_score)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatCurrency(device.balance || 0)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {Number(device.total_usage ?? 0).toFixed(2)} m³
                      </div>
                      <div className="text-xs text-gray-500">
                        {Number(device.avg_per_day ?? 0).toFixed(2)} m³/day
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleDeviceClick(device)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="View Details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => handleEditDevice(device)}
                              className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition"
                              title="Edit"
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleEditBalance(device)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                              title="Update Balance"
                            >
                              <DollarSign className="w-5 h-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Batch Operation Modal */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Batch Operations</h3>
            <p className="text-gray-600 mb-6">{selectedDevices.length} device(s) selected</p>
            
            <div className="space-y-3">
              <button
                onClick={() => handleBatchOperation('activate')}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Activate Devices
              </button>
              <button
                onClick={() => handleBatchOperation('deactivate')}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Deactivate Devices
              </button>
              <button
                onClick={() => handleBatchOperation('maintenance')}
                className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
              >
                Set to Maintenance
              </button>
              <button
                onClick={() => setShowBatchModal(false)}
                className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Device Detail Modal */}
      {showDetailModal && selectedDevice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{selectedDevice.device_name}</h3>
                <p className="text-gray-600">{selectedDevice.device_id}</p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Device Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedDevice.status)}`}>
                      {selectedDevice.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Device Type:</span>
                    <span className="font-medium">{selectedDevice.device_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Installation Date:</span>
                    <span className="font-medium">
                      {new Date(selectedDevice.installed_at).toLocaleDateString('id-ID')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Firmware:</span>
                    <span className="font-medium">{selectedDevice.firmware_version || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Health & Status */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Health & Performance</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Health Score:</span>
                    <span className={`font-bold ${getHealthColor(selectedDevice.health_score)}`}>
                      {selectedDevice.health_score?.toFixed(0) || 0}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Connection:</span>
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${getConnectionColor(selectedDevice.iot_device?.connection_status || 'offline')}`}></span>
                      <span className="font-medium capitalize">{selectedDevice.iot_device?.connection_status || 'offline'}</span>
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Uptime:</span>
                      <span className="font-medium">{selectedDevice.iot_device?.uptime?.toFixed(1) || 0}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Active Alerts:</span>
                    <span className="font-medium text-red-600">{selectedDevice.alert_count || 0}</span>
                  </div>
                </div>
              </div>

              {/* Usage Stats */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Usage Statistics</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Usage:</span>
                      <span className="font-medium">{Number(selectedDevice.total_usage ?? 0).toFixed(2)} m³</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Avg Daily:</span>
                      <span className="font-medium">{Number(selectedDevice.avg_per_day ?? 0).toFixed(2)} m³</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Peak Usage:</span>
                      <span className="font-medium">{Number(selectedDevice.peak_usage ?? 0).toFixed(2)} m³</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Cost:</span>
                    <span className="font-medium">Rp {(selectedDevice.stats?.total_cost || 0).toLocaleString('id-ID')}</span>
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Customer & Property</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Customer:</span>
                    <span className="font-medium">{selectedDevice.customer.full_name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Phone:</span>
                    <span className="font-medium">{selectedDevice.customer.phone || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Property:</span>
                    <span className="font-medium">{selectedDevice.property.property_name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Balance:</span>
                    <span className="font-medium">Rp {(selectedDevice.balance || 0).toLocaleString('id-ID')}</span>
                  </div>
                </div>
              </div>
            </div>

            {selectedDevice.notes && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Notes</h4>
                <p className="text-sm text-gray-600">{selectedDevice.notes}</p>
              </div>
            )}
          </div>
        </div>
      )}


      {/* Device Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Edit Device
              </h2>
            </div>

            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer *
                  </label>
                  <select
                    value={formEditData.customer_id}
                    onChange={(e) => setFormEditData({ ...formEditData, customer_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    data-testid="customer-id-select">
                    <option></option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>{customer.full_name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Property Type *
                  </label>
                  <select
                    value={formEditData.property_id}
                    onChange={(e) => setFormEditData({ ...formEditData, property_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    data-testid="property-id-select">
                    <option></option>
                    {properties.map(property => (
                      <option key={property.id} value={property.id}>{property.property_name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Device ID *
                  </label>
                  <input
                    type="text"
                    value={formEditData.device_number}
                    onChange={(e) => setFormEditData({ ...formEditData, device_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Serial Number *
                  </label>
                  <input
                    type="text"
                    value={formEditData.serial_number}
                    onChange={(e) => setFormEditData({ ...formEditData, serial_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Device Name *
                  </label>
                  <input
                    type="text"
                    value={formEditData.device_name}
                    onChange={(e) => setFormEditData({ ...formEditData, device_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Device Type *
                  </label>
                  <select
                    value={formEditData.device_type}
                    onChange={(e) => setFormEditData({ ...formEditData, device_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    data-testid="device-type-id-select">
                    <option></option>
                    {deviceOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Firmware Version *
                  </label>
                  <input
                    type="text"
                    value={formEditData.firmware_version}
                    onChange={(e) => setFormEditData({ ...formEditData, firmware_version: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hardware Version *
                  </label>
                  <input
                    type="text"
                    value={formEditData.hardware_version}
                    onChange={(e) => setFormEditData({ ...formEditData, hardware_version: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Installation Date
                  </label>
                  <input
                    type="date"
                    value={formEditData.installation_date}
                    onChange={(e) => setFormEditData({ ...formEditData, installation_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                  data-testid="cancel-property-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                  data-testid="submit-property-button"
                >
                  Update Device
                </button>
              </div>
            </form>

          </div>
        </div>
      )}

      {/* Device Edit Modal */}
      {showEditBalanceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Edit Device Balance
              </h2>
            </div>

            <form onSubmit={handleUpdateBalance} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Balance
                  </label>
                  <input
                    type="number"
                    value={formEditBalance.balance}
                    onChange={(e) => setFormEditBalance({ ...formEditBalance, balance: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeEditBalanceModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                  data-testid="cancel-property-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                  data-testid="submit-property-button"
                >
                  Update Balance
                </button>
              </div>
            </form>

          </div>
        </div>
      )}
      </div>
    </Layout>
  );
};

export default Devices;
